import { useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";

import { cn } from "../lib/cn";

export interface PinInputProps {
  length?: number;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Déclenché quand toutes les cases sont remplies — pour valider sans bouton. */
  onComplete?: (value: string) => void;
  /** `numeric` restreint aux chiffres et ouvre le pavé numérique sur mobile. */
  type?: "numeric" | "alphanumeric";
  /** Masque les caractères, pour un code sensible. */
  masked?: boolean;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
}

/**
 * Saisie de code à usage unique (OTP).
 *
 * Trois détails séparent une implémentation correcte d'une implémentation
 * pénible, et ce sont précisément ceux qu'on oublie en le refaisant à la main :
 *
 *   1. Le COLLER doit répartir le code sur toutes les cases. C'est le geste le
 *      plus fréquent — on copie le code depuis un SMS ou un e-mail.
 *   2. `autocomplete="one-time-code"` fait proposer le code par iOS et Android
 *      directement au-dessus du clavier.
 *   3. Retour arrière sur une case vide doit revenir à la précédente, sinon on
 *      reste coincé.
 */
export function PinInput({
  length = 6,
  value,
  defaultValue = "",
  onValueChange,
  onComplete,
  type = "numeric",
  masked = false,
  disabled,
  className,
  "aria-label": ariaLabel = "Code de vérification",
}: PinInputProps) {
  const [internal, setInternal] = useState(defaultValue);
  const isControlled = value !== undefined;
  const current = (isControlled ? value : internal).slice(0, length);

  const refs = useRef<(HTMLInputElement | null)[]>([]);

  const commit = (next: string) => {
    const trimmed = next.slice(0, length);
    if (!isControlled) setInternal(trimmed);
    onValueChange?.(trimmed);
    if (trimmed.length === length) onComplete?.(trimmed);
  };

  const focusAt = (index: number) => {
    refs.current[Math.min(Math.max(index, 0), length - 1)]?.focus();
  };

  const isAllowed = (char: string) =>
    type === "numeric" ? /^\d$/.test(char) : /^[a-zA-Z0-9]$/.test(char);

  const handleInput = (index: number, raw: string) => {
    // On ne garde que le dernier caractère saisi : sur mobile, le champ peut
    // recevoir l'ancienne valeur suivie de la nouvelle.
    const char = raw.slice(-1);
    if (char && !isAllowed(char)) return;

    const chars = current.padEnd(length, " ").split("");
    chars[index] = char || " ";
    commit(chars.join("").trimEnd());

    if (char) focusAt(index + 1);
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !current[index]) {
      event.preventDefault();
      focusAt(index - 1);
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusAt(index - 1);
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      focusAt(index + 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData("text")
      .split("")
      .filter(isAllowed)
      .slice(0, length)
      .join("");
    if (!pasted) return;

    commit(pasted);
    focusAt(pasted.length);
  };

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn("flex items-center gap-2", className)}
    >
      {Array.from({ length }, (_, index) => {
        const char = current[index] ?? "";
        return (
          <input
            key={index}
            ref={(element) => {
              refs.current[index] = element;
            }}
            type={masked && char ? "password" : "text"}
            inputMode={type === "numeric" ? "numeric" : "text"}
            // Sur la première case uniquement : c'est elle qui reçoit la
            // suggestion du système.
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={1}
            disabled={disabled}
            value={char.trim()}
            aria-label={`Caractère ${index + 1} sur ${length}`}
            onChange={(event) => handleInput(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={handlePaste}
            // Sélectionner au focus : retaper remplace au lieu d'ajouter.
            onFocus={(event) => event.target.select()}
            className={cn(
              "size-11 rounded-md border text-center font-mono text-lg text-fg",
              "border-border bg-surface-sunken",
              "transform-gpu transition-[background-color,border-color,transform] duration-fast ease-standard",
              "hover:border-border-strong",
              "focus:border-accent focus:bg-surface-raised focus:ring-[3px] focus:ring-accent/20 focus:outline-none",
              // La case remplie s'affirme : on suit sa progression d'un coup d'œil.
              char && "border-accent/40 bg-surface-raised",
              "contrast-more:border-border-strong",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          />
        );
      })}
    </div>
  );
}
